<script setup lang="ts">
/** Sticker aus der Asset-Bibliothek (Admin → Sticker & Assets): nach Kategorie filtern, suchen, per Klick einfügen. */
import { computed, onMounted, ref } from 'vue'
import { Search } from 'lucide-vue-next'
import { listAssets, type DesignAssetSummary } from '../../../services/designApi'
import { getWidgetConfig } from '../../../runtime/widgetConfig'
import { useDesignStore } from '../../../store/useDesignStore'

const props = defineProps<{ productId?: number | null }>()
const store = useDesignStore()
const cfg = getWidgetConfig() as any

const items = ref<DesignAssetSummary[]>([])
const categories = ref<Array<{ id: number; name: string }>>([])
const loading = ref(true)
const error = ref('')
const activeCategory = ref<number | null>(null)
const q = ref('')

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await listAssets({ editorBaseUrl: cfg.editorBaseUrl, productId: props.productId ?? null, type: 'sticker' })
    items.value = res.items || []
    const used = new Set(items.value.map((a) => a.categoryId).filter((id) => id != null))
    categories.value = (res.categories || []).filter((c) => used.has(c.id))
  } catch (e: any) {
    error.value = e?.message || 'Sticker konnten nicht geladen werden.'
  } finally {
    loading.value = false
  }
}
onMounted(load)

const visible = computed(() => {
  const s = q.value.trim().toLowerCase()
  return items.value.filter((a) =>
    (activeCategory.value == null || a.categoryId === activeCategory.value)
    && (!s || a.name.toLowerCase().includes(s) || (a.tags || []).some((t) => t.toLowerCase().includes(s))))
})
const assetUrl = (a: DesignAssetSummary) => a.svgUrl || a.pngUrl || a.url || ''

function add(a: DesignAssetSummary) {
  const url = assetUrl(a)
  if (!url) return
  store.addStickerLayer({ id: a.id, url, defaultWidthMm: a.defaultWidthMm, aspectRatio: a.aspectRatio })
}
</script>

<template>
  <div class="stp">
    <label class="stp__search">
      <Search :size="16" aria-hidden="true" />
      <input v-model="q" type="search" placeholder="Sticker suchen…" aria-label="Sticker suchen" />
    </label>

    <div v-if="categories.length > 1" class="stp__cats" role="tablist" aria-label="Kategorien">
      <button type="button" class="stp__cat" :class="{ 'stp__cat--on': activeCategory == null }" @click="activeCategory = null">Alle</button>
      <button v-for="c in categories" :key="c.id" type="button" class="stp__cat" :class="{ 'stp__cat--on': activeCategory === c.id }" @click="activeCategory = c.id">{{ c.name }}</button>
    </div>

    <p class="stp__hint">Klicken zum Einfügen. Danach auf der Fläche verschieben, skalieren und drehen.</p>

    <div v-if="loading" class="stp__state">Sticker werden geladen…</div>
    <div v-else-if="error" class="stp__state stp__state--err">{{ error }} <button type="button" @click="load">Erneut versuchen</button></div>
    <div v-else-if="!visible.length" class="stp__state">{{ items.length ? 'Keine Sticker gefunden.' : 'Noch keine Sticker verfügbar.' }}</div>
    <ul v-else class="stp__grid">
      <li v-for="a in visible" :key="a.id">
        <button type="button" class="stp__item" :title="a.name" :aria-label="`${a.name} einfügen`" @click="add(a)">
          <img :src="a.previewUrl || assetUrl(a)" :alt="a.name" loading="lazy" draggable="false" />
          <span class="stp__name">{{ a.name }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.stp { display: grid; gap: 12px; padding: 14px 16px; }
.stp__search { display: flex; align-items: center; gap: 8px; height: 36px; padding: 0 10px; border: 1px solid rgba(15, 23, 42, 0.16); border-radius: 6px; color: rgba(15, 23, 42, 0.5); }
.stp__search:focus-within { outline: 2px solid var(--pfe-color-accent, #e30b17); outline-offset: 1px; }
.stp__search input { flex: 1; min-width: 0; border: 0; outline: 0; font: inherit; font-size: 13px; background: transparent; color: #0f172a; }
.stp__cats { display: flex; flex-wrap: wrap; gap: 6px; }
.stp__cat { height: 28px; padding: 0 10px; border: 1px solid rgba(15, 23, 42, 0.16); border-radius: 999px; background: #fff; font: inherit; font-size: 12px; cursor: pointer; color: rgba(15, 23, 42, 0.78); }
.stp__cat--on { border-color: var(--pfe-color-accent, #e30b17); background: rgba(227, 11, 23, 0.08); color: var(--pfe-color-accent, #e30b17); font-weight: 600; }
.stp__hint { margin: 0; font-size: 12px; color: rgba(15, 23, 42, 0.6); }
.stp__state { padding: 16px 0; font-size: 13px; color: rgba(15, 23, 42, 0.6); }
.stp__state--err { color: var(--pfe-color-accent, #e30b17); }
.stp__grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.stp__item { display: flex; flex-direction: column; align-items: center; gap: 4px; width: 100%; padding: 8px 6px 6px; border: 1px solid rgba(15, 23, 42, 0.1); border-radius: 8px; background: #fff; cursor: pointer; font: inherit; transition: border-color 100ms ease, transform 100ms ease; }
.stp__item:hover { border-color: rgba(15, 23, 42, 0.3); transform: translateY(-1px); }
.stp__item:focus-visible { outline: 2px solid var(--pfe-color-accent, #e30b17); outline-offset: 2px; }
.stp__item img { width: 100%; aspect-ratio: 1; object-fit: contain; }
.stp__name { max-width: 100%; font-size: 11px; color: rgba(15, 23, 42, 0.7); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
